//@ts-check
/// <reference path="./carousel.d.ts" />

/**
 * Generic Carousel Component
 * Handles carousel navigation, dots functionality, and touch gestures
 * Supports both horizontal (desktop) and vertical (mobile) modes
 */
export class GenericCarousel {
	/**
	 * Gets the default settings for the carousel
	 *
	 * @returns {Required<import('./carousel').CarouselOptions>}
	 */
	static get DEFAULT_SETTINGS() {
		return {
			carouselId: 'carousel',
			prefix: 'carousel',
			trackSelector: '.carousel-track',
			mobileBreakpoint: 768,
			cardsPerView: {
				mobile: 3,
				desktop: 'auto', // 'auto' calculates based on container width
			},
			cardDimensions: {
				mobile: { height: 280, gap: 16 },
				desktop: { width: 330, gap: 16 },
			},
			orientation: {
				mobile: 'vertical',
				desktop: 'horizontal',
			},
			swipeThreshold: 50,
			navThreshold: { desktop: 3, mobile: 1 },
			customElements: null,
		};
	}

	/**
	 * Creates a new GenericCarousel instance
	 * @param {import('./carousel').CarouselOptions} [options={}] - Configuration options
	 */
	constructor(options = {}) {
		// Set default options
		/** @private @readonly @type {Required<import('./carousel').CarouselOptions>} */
		this.options = {
			...GenericCarousel.DEFAULT_SETTINGS,
			...options,
		};

		// We need to make sure that we have the dimensions and gap we require for all orientations
		for (let key of Object.keys(this.options.orientation)) {
			if (this.options.orientation[key] === 'vertical') {
				if (!this.options.cardDimensions[key].height) {
					throw new Error(`GenericCarousel: ${key} cardDimensions is missing the height`);
				}
			} else if (this.options.orientation[key] === 'horizontal') {
				if (!this.options.cardDimensions[key].width) {
					throw new Error(`GenericCarousel: ${key} cardDimensions is missing the width`);
				}
			} else {
				// Should never get here!
				throw new Error(`GenericCarousel: ${key} orientation ${this.options.orientation[key]} is invalid`);
			}
		}

		/** @private @readonly @type {HTMLElement|null} */
		this.carousel = this.options.customElements?.carousel ?? document.getElementById(this.options.carouselId);

		// Throw if carousel doesn't exist
		if (!this.carousel) throw new Error(`GenericCarousel: Element with id '${this.options.carouselId}' not found.`);

		/** @private @readonly @type {HTMLElement|null} */
		this.track = this.options.customElements?.track ?? this.carousel.querySelector(this.options.trackSelector);

		if (!this.track)
			throw new Error(`GenericCarousel: Track element '${this.options.trackSelector}' not found in carousel.`);

		/** @private @type {HTMLCollection} */
		this.cards = this.track.children;

		// Get navigation elements
		/** @private @readonly @type {HTMLElement|null} */
		this.desktopNav =
			this.options.customElements?.desktopNav ??
			document.querySelector(`.${this.options.prefix}-navigation-wrapper--desktop`);
		/** @private @readonly @type {HTMLElement|null} */
		this.mobileNav =
			this.options.customElements?.mobileNav ??
			document.querySelector(`.${this.options.prefix}-navigation-wrapper--mobile`);

		/** @private @readonly @type {NodeListOf<HTMLButtonElement>} */
		//@ts-ignore
		this.prevBtns =
			this.options.customElements?.prevBtns ?? document.querySelectorAll(`.${this.options.prefix}-prev`);
		/** @private @readonly @type {NodeListOf<HTMLButtonElement>} */
		//@ts-ignore
		this.nextBtns =
			this.options.customElements?.nextBtns ?? document.querySelectorAll(`.${this.options.prefix}-next`);
		/** @private @readonly @type {HTMLElement|null} */
		this.dotsContainer =
			this.options.customElements?.dotsContainer ??
			document.getElementById(`${this.options.prefix}-dots-desktop`);
		/** @private @readonly @type {HTMLElement|null} */
		this.dotsContainerMobile =
			this.options.customElements?.dotsContainerMobile ??
			document.getElementById(`${this.options.prefix}-dots-mobile`);

		// Exit if no navigation exists
		if (!this.desktopNav && !this.mobileNav) {
			return;
		}

		/** @private @type {number} */
		this.currentIndex = 0;
		/** @private @type {number} */
		this.maxIndex = 0;
		/** @private @type {boolean} */
		this.isMobile = window.innerWidth <= this.options.mobileBreakpoint;
		/** @private @type {number} */
		this.cardHeight = 0;
		/** @private @type {number} */
		this.cardWidth = 0;
		/** @private @type {number} */
		this.visibleCards = 0;
		/** @private @type {HTMLCollection|null} */
		this.dots = this.dotsContainer ? this.dotsContainer.children : null;
		/** @private @type {HTMLCollection|null} */
		this.dotsMobile = this.dotsContainerMobile ? this.dotsContainerMobile.children : null;

		this.#init();
	}

	/**
	 * Initializes the carousel component
	 */
	#init() {
		this.#calculateDimensions();
		this.#createDots();
		this.#bindEvents();

		// Update navigation visibility based on whether scrolling is needed
		this.#updateNavigationVisibility();

		// Initialize carousel if navigation is needed
		if (this.maxIndex > 0) {
			this.#updateCarousel();
		}
	}

	/**
	 * Gets the current orientation of the carousel, based on the device.
	 *
	 * @returns {typeof GenericCarousel.DEFAULT_SETTINGS.orientation.mobile | typeof GenericCarousel.DEFAULT_SETTINGS.orientation.desktop}
	 */
	#getOrientation() {
		let isMobile = window.innerWidth <= this.options.mobileBreakpoint;

		return isMobile ?
				(this.options.orientation?.mobile ?? GenericCarousel.DEFAULT_SETTINGS.orientation.mobile)
			:	(this.options.orientation?.desktop ?? GenericCarousel.DEFAULT_SETTINGS.orientation.desktop);
	}

	/**
	 * Calculates carousel dimensions based on current viewport
	 */
	#calculateDimensions() {
		this.isMobile = window.innerWidth <= this.options.mobileBreakpoint;

		const orientation = this.#getOrientation();
		let device = this.isMobile ? 'mobile' : 'desktop';

		let dimensions = this.options.cardDimensions[this.isMobile ? 'mobile' : 'desktop'];

		/**
		 * @type {(
		 *  typeof GenericCarousel.DEFAULT_SETTINGS.cardsPerView.desktop
		 *  | typeof GenericCarousel.DEFAULT_SETTINGS.cardsPerView.mobile
		 * )}
		 */
		let visibleCards = this.options.cardsPerView[device] ?? GenericCarousel.DEFAULT_SETTINGS.cardsPerView[device];

		if (orientation === 'horizontal') {
			this.cardWidth =
				(dimensions.width ?? GenericCarousel.DEFAULT_SETTINGS.cardDimensions[device].width ?? 0) +
				dimensions.gap;
			this.cardHeight = 0;

			if (visibleCards === 'auto') {
				visibleCards =
					this.carousel ? Math.floor(this.carousel.offsetWidth / this.cardWidth)
					: device === 'desktop' ? 1
					: 3;
			}
		} else if (orientation === 'vertical') {
			this.cardWidth = 0;
			this.cardHeight =
				(dimensions.height ?? GenericCarousel.DEFAULT_SETTINGS.cardDimensions[device].height ?? 0) +
				dimensions.gap;

			if (visibleCards === 'auto') {
				visibleCards =
					device === 'desktop' && this.carousel ?
						Math.floor(this.carousel.offsetHeight / this.cardHeight)
					:	3;
			}
		}

		if (visibleCards === 'auto') {
			// Something bad happened ... this should be impossible!
			visibleCards = orientation === 'vertical' || device === 'desktop' ? 3 : 1;
		}

		this.visibleCards = visibleCards;
		this.maxIndex = Math.max(0, this.cards.length - visibleCards);

		/*if (this.isMobile) {
      // Mobile: vertical layout
      const mobileDims =
        this.options.cardDimensions && this.options.cardDimensions.mobile
          ? this.options.cardDimensions.mobile
          : { height: 280, gap: 16 };
      const cardHeight =
        (typeof mobileDims.height === 'number' ? mobileDims.height : 280) +
        (typeof mobileDims.gap === 'number' ? mobileDims.gap : 16);
      const visibleCards =
        this.options.cardsPerView && typeof this.options.cardsPerView.mobile === 'number'
          ? this.options.cardsPerView.mobile
          : 3;
      this.maxIndex = Math.max(0, this.cards.length - visibleCards);
      this.cardHeight = cardHeight;
      this.cardWidth = 0;
      this.visibleCards = visibleCards;
    } else {
      // Desktop: horizontal layout
      const desktopDims =
        this.options.cardDimensions && this.options.cardDimensions.desktop
          ? this.options.cardDimensions.desktop
          : { width: 330, gap: 16 };
      const cardWidth =
        (typeof desktopDims.width === 'number' ? desktopDims.width : 330) +
        (typeof desktopDims.gap === 'number' ? desktopDims.gap : 16);
      let visibleCards;
      if (this.options.cardsPerView && this.options.cardsPerView.desktop === 'auto') {
        visibleCards = this.carousel ? Math.floor(this.carousel.offsetWidth / cardWidth) : 1;
      } else {
        visibleCards =
          this.options.cardsPerView && typeof this.options.cardsPerView.desktop === 'number'
            ? this.options.cardsPerView.desktop
            : 1;
      }
      visibleCards = typeof visibleCards === 'number' ? visibleCards : 1;
      this.maxIndex = Math.max(0, this.cards.length - visibleCards);
      this.cardWidth = cardWidth;
      this.cardHeight = 0;
      this.visibleCards = visibleCards;
    }*/
	}

	/**
	 * Updates navigation visibility based on device type and card count
	 * - Desktop: Show navigation only if more than options.navThreshold.desktop cards
	 * - Mobile: Show navigation if more than options.navThreshold.mobile cards
	 */
	#updateNavigationVisibility() {
		const cardCount = this.cards.length;
		const threshold = this.isMobile
			? this.options.navThreshold?.mobile ?? 1
			: this.options.navThreshold?.desktop ?? 3;
		const shouldShowNav = cardCount > threshold;

		// Update desktop navigation visibility
		if (this.desktopNav) {
			this.desktopNav.style.display = shouldShowNav ? '' : 'none';
		}

		// Update mobile navigation visibility
		if (this.mobileNav) {
			this.mobileNav.style.display = shouldShowNav ? '' : 'none';
		}
	}

	/**
	 * Creates navigation dots for the carousel
	 */
	#createDots() {
		// Desktop dots
		if (this.dotsContainer) {
			this.dotsContainer.innerHTML = '';
			for (let i = 0; i <= this.maxIndex; i++) {
				const dot = document.createElement('div');
				dot.classList.add('dot');
				if (i === 0) {
					dot.classList.add('active');
				}
				dot.addEventListener('click', () => this.goToSlide(i));
				this.dotsContainer.appendChild(dot);
			}
			// Update dots reference after re-creation
			this.dots = this.dotsContainer.children;
		} else {
			this.dots = null;
		}

		// Mobile dots
		if (this.dotsContainerMobile && this.dotsContainerMobile !== this.dotsContainer) {
			this.dotsContainerMobile.innerHTML = '';
			for (let i = 0; i <= this.maxIndex; i++) {
				const dot = document.createElement('div');
				dot.classList.add('dot');
				if (i === 0) {
					dot.classList.add('active');
				}
				dot.addEventListener('click', () => this.goToSlide(i));
				this.dotsContainerMobile.appendChild(dot);
			}
			// Update dotsMobile reference after re-creation
			this.dotsMobile = this.dotsContainerMobile.children;
		} else {
			this.dotsMobile = null;
		}
	}

	/**
	 * Updates carousel position and navigation state
	 */
	#updateCarousel() {
		if (this.isMobile && this.options.orientation && this.options.orientation.mobile === 'vertical') {
			// Mobile: vertical movement
			const offset = -this.currentIndex * this.cardHeight;
			if (this.track) this.track.style.transform = `translateY(${offset}px)`;
		} else {
			// Desktop: horizontal movement
			const offset = -this.currentIndex * this.cardWidth;
			if (this.track) this.track.style.transform = `translateX(${offset}px)`;
		}

		// Update dots
		if (this.dots && this.dots.length > 0) {
			Array.from(this.dots).forEach((dot, index) => {
				dot.classList.toggle('active', index === this.currentIndex);
			});
		}
		if (this.dotsMobile && this.dotsMobile.length > 0) {
			Array.from(this.dotsMobile).forEach((dot, index) => {
				dot.classList.toggle('active', index === this.currentIndex);
			});
		}

		// Update button states
		if (this.prevBtns.length > 0) {
			this.prevBtns.forEach((btn) => {
				btn.disabled = this.currentIndex === 0;
			});
		}
		if (this.nextBtns.length > 0) {
			this.nextBtns.forEach((btn) => {
				btn.disabled = this.currentIndex >= this.maxIndex;
			});
		}
	}

	/**
	 * Navigates to a specific slide
	 * @public
	 * @param {number} index - Target slide index
	 */
	goToSlide(index) {
		this.currentIndex = Math.max(0, Math.min(index, this.maxIndex));
		this.#updateCarousel();
	}

	/**
	 * Refreshes the carousel internal information
	 * @public
	 *
	 * @returns {boolean}
	 */
	refresh() {
		if (!this.track) {
			return false;
		}

		this.cards = this.track.children;
		this.maxIndex = Math.max(0, this.cards.length - this.visibleCards);
		this.currentIndex = Math.max(0, Math.min(this.currentIndex, this.maxIndex));

		this.#calculateDimensions();
		this.#createDots();
		this.#updateNavigationVisibility();
		this.#updateCarousel();

		return true;
	}

	/**
	 * Binds event listeners for navigation and gestures
	 */
	#bindEvents() {
		// Previous button handlers
		if (this.prevBtns.length > 0) {
			this.prevBtns.forEach((btn) => {
				btn.addEventListener('click', () => {
					if (this.currentIndex > 0) {
						this.goToSlide(this.currentIndex - 1);
					}
				});
			});
		}

		// Next button handlers
		if (this.nextBtns.length > 0) {
			this.nextBtns.forEach((btn) => {
				btn.addEventListener('click', () => {
					if (this.currentIndex < this.maxIndex) {
						this.goToSlide(this.currentIndex + 1);
					}
				});
			});
		}

		// Touch/swipe support
		this.#bindTouchEvents();

		// Resize handler
		window.addEventListener('resize', () => this.#handleResize());
	}

	/**
	 * Binds touch/swipe event listeners
	 */
	#bindTouchEvents() {
		let startX = 0;
		let startY = 0;
		let isDragging = false;
		let allowPageScroll = false;

		if (!this.track) return;
		this.track.addEventListener('touchstart', (e) => {
			startX = e.touches[0].clientX;
			startY = e.touches[0].clientY;
			isDragging = true;
			allowPageScroll = false;
		});

		this.track.addEventListener('touchmove', (e) => {
			if (!isDragging) return;

			const currentX = e.touches[0].clientX;
			const currentY = e.touches[0].clientY;
			const diffX = startX - currentX;
			const diffY = startY - currentY;

			if (this.isMobile && this.options.orientation && this.options.orientation.mobile === 'vertical') {
				// Mobile vertical: check if we should allow page scroll
				const isSwipeUp = diffY > 0;
				const isSwipeDown = diffY < 0;

				if (
					(isSwipeUp && this.currentIndex >= this.maxIndex) ||
					(isSwipeDown && this.currentIndex === 0) ||
					this.maxIndex === 0
				) {
					allowPageScroll = true;
					return;
				}
				e.preventDefault();
			} else {
				// Desktop horizontal: prevent default if navigation exists
				if (this.maxIndex > 0) {
					e.preventDefault();
				}
			}
		});

		this.track.addEventListener('touchend', (e) => {
			if (!isDragging) return;

			if (allowPageScroll || this.maxIndex === 0) {
				isDragging = false;
				return;
			}

			const endX = e.changedTouches[0].clientX;
			const endY = e.changedTouches[0].clientY;

			if (this.isMobile && this.options.orientation && this.options.orientation.mobile === 'vertical') {
				// Mobile: vertical swipe
				const diffY = startY - endY;

				if (Math.abs(diffY) > this.options.swipeThreshold) {
					if (diffY > 0 && this.currentIndex < this.maxIndex) {
						this.goToSlide(this.currentIndex + 1);
					} else if (diffY < 0 && this.currentIndex > 0) {
						this.goToSlide(this.currentIndex - 1);
					}
				}
			} else {
				// Desktop: horizontal swipe
				const diffX = startX - endX;

				if (Math.abs(diffX) > this.options.swipeThreshold) {
					if (diffX > 0 && this.currentIndex < this.maxIndex) {
						this.goToSlide(this.currentIndex + 1);
					} else if (diffX < 0 && this.currentIndex > 0) {
						this.goToSlide(this.currentIndex - 1);
					}
				}
			}

			isDragging = false;
		});
	}

	/**
	 * Handles window resize events
	 */
	#handleResize() {
		const oldMobile = this.isMobile;
		this.#calculateDimensions();

		// If switching between mobile/desktop, reset position and recreate dots
		if (oldMobile !== this.isMobile) {
			this.currentIndex = 0;
			this.#createDots();
		} else if (this.currentIndex > this.maxIndex) {
			this.goToSlide(this.maxIndex);
		}

		// Update navigation visibility based on new dimensions
		this.#updateNavigationVisibility();

		// Update carousel if navigation is needed
		if (this.maxIndex > 0) {
			this.#updateCarousel();
		}
	}
}
